import Button from '@carvana/ds-button';
import { CheckCircleSpot } from '@carvana/ds-icons';
import Radio, { RadioGroup } from '@carvana/ds-radio';
import { icon } from '@carvana/ds-styles';
import TextArea from '@carvana/ds-text-area';
import { Formik, Form } from 'formik';
import { CircleAlert } from 'lucide-react';
import React, { useCallback, useContext, useState } from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { cancelOrder } from '|/app/services';
import { useBrand, useBrandCopy } from '|/brand';
import type { BrandCopy } from '|/brand';
import { FormStateContext } from '|/context';
import { isNativeWebview } from '|/helpers/is-native-app';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events';
import sendNativeEvent from '|/helpers/send-native-event';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import useSubmit from '|/hooks/use-submit';
import styles from './cancel-order-modal.module.css';
import validationSchema from './cancel-order-validation-schema';

interface CancelOrderModalDSProps {
  isOpen: boolean;
  onClose: () => void;
  shippingFee?: number;
  vehicleId?: number;
  isShippingFeeNonRefundable?: boolean;
}

const CANCEL_REASONS = [
  'Decided not to buy a car right now',
  'Switching to another Carvana car',
  'No longer fits my budget',
  'Appointment date has been delayed',
  'Poor customer experience',
  'Found a car elsewhere',
  'Other'
] as const;

type CancelReason = (typeof CANCEL_REASONS)[number] | BrandCopy['switchVehicleCancelReason'];

interface FormValues {
  reason: CancelReason | '';
  otherReasonText: string;
}

const initialValues: FormValues = {
  reason: '',
  otherReasonText: ''
};

const CancelOrderModal = ({
  isOpen,
  onClose,
  shippingFee = 0,
  vehicleId,
  isShippingFeeNonRefundable = false
}: CancelOrderModalDSProps) => {
  const { trackEvent } = useAnalyticsContext();
  const { isSubmitting } = useContext(FormStateContext);
  const { brand, identity } = useBrand();
  const copy = useBrandCopy();
  const submit = useSubmit();
  const [isCancelled, setIsCancelled] = useState(false);

  const cancelReasons: CancelReason[] = CANCEL_REASONS.map(reason =>
    reason === 'Switching to another Carvana car' ? copy.switchVehicleCancelReason : reason
  );

  const handleSubmit = useCallback(
    (values: FormValues) => {
      trackEvent('Cancel option selected', {
        cancelOption: values.reason,
        otherText: values.reason === 'Other' ? values.otherReasonText : '',
        vehicleId
      });
      trackEvent('v2_opd_cancel_order_button_tap');
      trackEvent('pp_clicked_cancel_purchase');

      const abortController = new AbortController();

      submit(
        cancelOrder({
          reasons: ['CustomerSelfCancelled'],
          notes: values.reason === 'Other' ? values.otherReasonText : values.reason
        }).then(() => {
          setIsCancelled(true);
        }),
        abortController.signal
      );
    },
    [submit, trackEvent, vehicleId]
  );

  const handleModalClose = useCallback(() => {
    trackEvent('pp_cancel_order_modal_closed');
    onClose();
  }, [onClose, trackEvent]);

  const handleGoBack = useCallback(() => {
    trackEvent('pp_cancel_order_go_back');
    onClose();
  }, [onClose, trackEvent]);

  const formatMoney = (number: number) =>
    number.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });

  const shippingFeeFormatted = formatMoney(shippingFee);

  const redirectToInventory = () => {
    if (isNativeWebview()) {
      sendNativeEvent({
        event: MobileWebviewEvents.CONTINUE_TO_SRP,
        url: `${process.env.BASE_URL}/cars`,
        source: 'purchase'
      });
      return;
    }
    window.location.href = `${process.env.BASE_URL}/cars`;
  };

  const redirectToSlate = () => {
    window.location.href = identity.homeUrl;
  };

  if (isCancelled) {
    return (
      <EventedModal
        isOpen={isOpen}
        analyticsId={ModalNames.CancelOrder}
        modalHeader="Cancel order"
        onClose={handleModalClose}
        desktopWidth="480px"
        mobileStyle="full"
        closeIcon={false}
        disableOutsideClose
      >
        <div className={styles.content}>
          <div className={styles.successSection}>
            <CheckCircleSpot className={styles.successIcon} color={icon.primary} size={48} />
            <h3 className={styles.successTitle}>Your order has been cancelled.</h3>
            <p className={styles.successDescription}>{copy.cancelOrderSuccessDescription}</p>
            <div className={styles.successFooter}>
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={brand === 'slate' ? redirectToSlate : redirectToInventory}
                data-analytics-id={identity.cancelOrderPrimaryActionAnalyticsId}
                className={styles.fullWidthButton}
              >
                {copy.cancelOrderPrimaryAction}
              </Button>
            </div>
          </div>
        </div>
      </EventedModal>
    );
  }

  return (
    <EventedModal
      isOpen={isOpen}
      analyticsId={ModalNames.CancelOrder}
      modalHeader="Cancel order"
      onClose={handleModalClose}
      desktopWidth="480px"
      mobileStyle="full"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, handleChange, handleBlur }) => (
          <Form>
            <div className={`${styles.content} ${styles.modalContentPadding}`}>
              <div className={styles.alertSection}>
                <div className={styles.alertHeader}>
                  <CircleAlert className={styles.alertIcon} color={icon.warning} />
                  <h3 className={styles.alertTitle}>Are you sure you want to cancel your order?</h3>
                </div>
                <p className={styles.alertDescription}>{copy.cancelOrderWarningDescription}</p>
                {shippingFee > 0 && isShippingFeeNonRefundable && (
                  <p className={styles.alertDescription}>
                    Your <strong>{shippingFeeFormatted}</strong> shipping fee may not be eligible
                    for a refund.
                  </p>
                )}
              </div>

              <div className={styles.reasonsSection}>
                <RadioGroup
                  aria-label="Reason for canceling your order"
                  value={values.reason as CancelReason}
                  onChange={(reason: CancelReason) => {
                    setFieldValue('reason', reason);
                    trackEvent('pp_cancel_order_reason_selected', { reason });
                  }}
                  className={styles.radioGroup}
                >
                  {cancelReasons.map(reason => (
                    <Radio
                      key={reason}
                      id={`cancel-reason-${reason.toLowerCase().replace(/\s+/g, '-')}`}
                      value={reason}
                      selected={values.reason === reason}
                      data-analytics-id={`cancel-reason-${reason.toLowerCase().replace(/\s+/g, '-')}`}
                      className={styles.radioOption}
                    >
                      {reason}
                    </Radio>
                  ))}
                </RadioGroup>

                {values.reason === 'Other' && (
                  <div className={styles.textAreaSection}>
                    <TextArea
                      id="cancel-reason-other-text"
                      name="otherReasonText"
                      aria-label="Tell us more about why you're canceling"
                      placeholder="Add a message here"
                      value={values.otherReasonText}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      minRows={3}
                      maxRows={6}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className={styles.stickyButtonContainer}>
              <div className={styles.footer}>
                <Button
                  type="submit"
                  variant="tertiary"
                  size="md"
                  data-analytics-id="cancel-order-confirm"
                  disabled={!values.reason || isSubmitting}
                  isLoading={isSubmitting}
                >
                  Yes, cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={handleGoBack}
                  disabled={isSubmitting}
                  data-analytics-id="cancel-order-go-back"
                >
                  No, go back
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </EventedModal>
  );
};

export default CancelOrderModal;
